import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Row, Col, Table } from 'reactstrap';
import { hasActiveAccess, getSpecialtyFields } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

// Fake preview content - looks real but contains no actual data
const MockQuestionBankPreview = () => {
    const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
    const fakeQuestions = [
        { id: 'Q-2847', subject: 'Musculoskeletal', unit: 'Spine Disorders', difficulty: 'Medium', status: 'New' },
        { id: 'Q-1923', subject: 'Neuromuscular', unit: 'Peripheral Neuropathy', difficulty: 'Hard', status: 'Attempted' },
        { id: 'Q-3651', subject: 'Pain Management', unit: 'Chronic Pain Syndromes', difficulty: 'Easy', status: 'New' },
        { id: 'Q-0892', subject: 'Stroke Rehabilitation', unit: 'Motor Recovery', difficulty: 'Medium', status: 'Correct' },
        { id: 'Q-4215', subject: 'Spinal Cord Injury', unit: 'Autonomic Dysreflexia', difficulty: 'Hard', status: 'Incorrect' },
        { id: 'Q-1567', subject: 'Pediatric Rehab', unit: 'Cerebral Palsy', difficulty: 'Medium', status: 'New' },
        { id: 'Q-3298', subject: 'Traumatic Brain Injury', unit: 'Cognitive Rehabilitation', difficulty: 'Hard', status: 'New' },
        { id: 'Q-0734', subject: 'Electrodiagnosis', unit: 'Nerve Conduction Studies', difficulty: 'Medium', status: 'Attempted' },
    ];

    const getDifficultyBadge = (difficulty) => {
        const colors = { Easy: 'success', Medium: 'warning', Hard: 'danger' };
        return <span className={`badge badge-${colors[difficulty]}`}>{difficulty}</span>;
    };

    const getStatusBadge = (status) => {
        const colors = { New: 'info', Attempted: 'warning', Correct: 'success', Incorrect: 'danger' };
        return <span className={`badge badge-${colors[status]}`}>{status}</span>;
    };

    return (
        <div className="container-fluid mt--7">
            <Row>
                <Col lg="12">
                    <Card className="shadow">
                        <CardHeader className="bg-transparent">
                            <Row className="align-items-center">
                                <Col>
                                    <h2 className="mb-0">
                                        <i className="fas fa-book-medical mr-2 text-primary"></i>
                                        Question Bank
                                    </h2>
                                    <p className="text-muted small mb-0 mt-1">
                                        Over 2,500+ board-style questions covering all {getSpecialty(activeSpecialty).name} topics
                                    </p>
                                </Col>
                                <Col className="text-right">
                                    <Button color="primary" size="sm">
                                        <i className="fas fa-plus mr-2"></i>
                                        Create Test
                                    </Button>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            {/* Fake filter section */}
                            <Row className="mb-4">
                                <Col md="3" className="mb-2">
                                    <label className="form-control-label small">Subject</label>
                                    <div className="border rounded p-2 bg-white">
                                        <span className="text-muted">All Subjects</span>
                                    </div>
                                </Col>
                                <Col md="3" className="mb-2">
                                    <label className="form-control-label small">Unit</label>
                                    <div className="border rounded p-2 bg-white">
                                        <span className="text-muted">All Units</span>
                                    </div>
                                </Col>
                                <Col md="3" className="mb-2">
                                    <label className="form-control-label small">Difficulty</label>
                                    <div className="border rounded p-2 bg-white">
                                        <span className="text-muted">All Levels</span>
                                    </div>
                                </Col>
                                <Col md="3" className="mb-2">
                                    <label className="form-control-label small">Status</label>
                                    <div className="border rounded p-2 bg-white">
                                        <span className="text-muted">All Status</span>
                                    </div>
                                </Col>
                            </Row>

                            {/* Stats cards */}
                            <Row className="mb-4">
                                <Col md="3" sm="6" className="mb-3">
                                    <Card className="bg-gradient-primary text-white">
                                        <CardBody className="py-3">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <p className="text-white small mb-0">Total Questions</p>
                                                    <h3 className="text-white mb-0">2,547</h3>
                                                </div>
                                                <i className="fas fa-question-circle fa-2x opacity-5"></i>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md="3" sm="6" className="mb-3">
                                    <Card className="bg-gradient-success text-white">
                                        <CardBody className="py-3">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <p className="text-white small mb-0">Correct</p>
                                                    <h3 className="text-white mb-0">--</h3>
                                                </div>
                                                <i className="fas fa-check-circle fa-2x opacity-5"></i>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md="3" sm="6" className="mb-3">
                                    <Card className="bg-gradient-danger text-white">
                                        <CardBody className="py-3">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <p className="text-white small mb-0">Incorrect</p>
                                                    <h3 className="text-white mb-0">--</h3>
                                                </div>
                                                <i className="fas fa-times-circle fa-2x opacity-5"></i>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md="3" sm="6" className="mb-3">
                                    <Card className="bg-gradient-info text-white">
                                        <CardBody className="py-3">
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <p className="text-white small mb-0">Unanswered</p>
                                                    <h3 className="text-white mb-0">2,547</h3>
                                                </div>
                                                <i className="fas fa-clock fa-2x opacity-5"></i>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Fake questions table */}
                            <Table className="align-items-center table-hover" responsive>
                                <thead className="thead-light">
                                    <tr>
                                        <th>Question ID</th>
                                        <th>Subject</th>
                                        <th>Unit</th>
                                        <th>Difficulty</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fakeQuestions.map((q, i) => (
                                        <tr key={i}>
                                            <td><span className="font-weight-bold text-primary">{q.id}</span></td>
                                            <td>{q.subject}</td>
                                            <td><span className="text-muted">{q.unit}</span></td>
                                            <td>{getDifficultyBadge(q.difficulty)}</td>
                                            <td>{getStatusBadge(q.status)}</td>
                                            <td>
                                                <Button color="primary" size="sm" outline>
                                                    <i className="fas fa-eye"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <span className="text-muted small">Showing 1-8 of 2,547 questions</span>
                                <div>
                                    <Button color="secondary" size="sm" className="mr-2" disabled>Previous</Button>
                                    <Button color="primary" size="sm">Next</Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

const AccessRestriction = ({ children, userDetails }) => {
    const history = useHistory();
    const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
    const hasAccess = hasActiveAccess(userDetails, activeSpecialty);

    // If user has access, render the actual content
    if (hasAccess) {
        return children;
    }

    // If no access, render FAKE preview (not the real content) with upgrade prompt
    // This way, even if they inspect/remove CSS, there's no real data
    return (
        <div style={{ position: 'relative', minHeight: '400px' }}>
            {/* FAKE preview content - NOT the real children */}
            <div style={{
                filter: 'blur(3px)',
                pointerEvents: 'none',
                userSelect: 'none',
                opacity: 0.7
            }}>
                <MockQuestionBankPreview />
            </div>

            {/* Upgrade prompt */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                zIndex: 10,
                borderRadius: '12px',
                padding: '30px 50px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
                <i className="fas fa-lock text-warning" style={{ fontSize: '40px', marginBottom: '15px' }}></i>
                <h3 className="text-dark mb-2">Access Required</h3>
                <p className="text-muted mb-3 text-center" style={{ maxWidth: '300px', fontSize: '14px' }}>
                    {userDetails?.[getSpecialtyFields(activeSpecialty).trialStartDate]
                        ? 'Your free trial has expired.'
                        : 'Subscribe to access the Question Bank.'}
                </p>
                <Button
                    color="success"
                    size="lg"
                    onClick={() => history.push('/client/plans')}
                >
                    <i className="fas fa-crown mr-2"></i>
                    View Plans
                </Button>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    userDetails: state.loginAuth.userDetails
});

export default connect(mapStateToProps)(AccessRestriction);
